import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';

export default function About() {
  const { t } = useTranslation();

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '蘑菇影视首页',
        item: 'https://mogutv.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于蘑菇影视',
        item: 'https://mogutv.com/about',
      },
    ],
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于蘑菇影视',
    description: '蘑菇影视成立于2020年，是国内领先的高清在线视频播放平台。致力于为用户提供最优质的影视内容服务，涵盖电影、电视剧、综艺、动漫等多个品类。',
    url: 'https://mogutv.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '蘑菇影视',
      url: 'https://mogutv.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '蘑菇影视是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蘑菇影视是国内领先的高清在线视频播放平台，为用户提供电影、电视剧、综艺、动漫等海量高清免费在线观看服务，采用先进视频编码技术和全球CDN加速，确保播放流畅不缓冲。',
        },
      },
      {
        '@type': 'Question',
        name: '蘑菇影视需要付费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蘑菇影视承诺为用户提供完全免费的观影服务。所有电影、电视剧、综艺、动漫内容均可免费在线观看，无需任何付费或会员订阅。',
        },
      },
      {
        '@type': 'Question',
        name: '蘑菇影视支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蘑菇影视支持手机、平板、电脑等多种设备访问。无论您使用iOS、Android还是Windows系统，都可以通过浏览器直接访问蘑菇影视官网，享受流畅的观影体验。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于蘑菇影视 | 品牌介绍与联系方式"
        description="蘑菇影视成立于2020年，是国内领先的高清在线视频播放平台。致力于为用户提供最优质的影视内容服务，涵盖电影、电视剧、综艺、动漫等多个品类，拥有海量正版高清资源库。"
        canonicalUrl="https://mogutv.com/about"
        ogType="website"
        structuredData={{
          '@graph': [breadcrumbSchema, aboutSchema, faqSchema],
        }}
      />
      <Navbar />

      <main className="min-h-screen bg-background-50">
        {/* Breadcrumb */}
        <div className="pt-20 md:pt-24 pb-4 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">
                {t('common.breadcrumbHome')}
              </Link>
              <span>/</span>
              <span className="text-foreground-900 font-medium">
                {t('about.title')}
              </span>
            </nav>
          </div>
        </div>

        {/* Page Header */}
        <div className="py-8 md:py-12 bg-background-100">
          <div className="w-full px-4 md:px-6 text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
              {t('about.title')}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
              致力于为您提供最优质的在线影视观影体验
            </p>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                {t('about.brandIntro')}
              </h2>
            </div>

            <div className="bg-background-100 rounded-lg p-6 md:p-8">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                {t('about.brandDesc')}
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                自成立以来，蘑菇影视始终坚持"用户至上、内容为王"的核心理念。我们拥有专业的内容运营团队，每日精选热门电影、电视剧、综艺和动漫内容，确保用户能够第一时间获取最新最热的影视资源。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                我们采用先进的视频编码技术和全球CDN加速节点，确保用户无论身处何地，都能享受流畅不缓冲的高清观影体验。同时，我们的智能推荐算法能够根据用户的观影偏好，精准推送个性化内容。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {t('about.mission')}
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              {[
                { value: '10000+', label: '影视资源' },
                { value: '500万+', label: '日活用户' },
                { value: '99.9%', label: '服务可用率' },
                { value: '4.8', label: '用户评分' },
              ].map((stat, index) => (
                <div
                  key={index}
                  className="bg-background-100 rounded-lg p-4 md:p-6 text-center"
                >
                  <div className={`text-xl md:text-2xl font-heading font-bold mb-1 ${
                    index === 0
                      ? 'text-primary-500'
                      : index === 1
                        ? 'text-accent-500'
                        : index === 2
                          ? 'text-secondary-500'
                          : 'text-foreground-900'
                  }`}>
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                {t('about.reputation')}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[
                {
                  name: '张先生',
                  role: '资深影迷',
                  content:
                    '蘑菇影视是我用过最流畅的视频网站，高清画质非常棒！资源更新也很快，新上映的电影基本都能第一时间看到。强烈推荐给所有影迷朋友。',
                  rating: 5,
                },
                {
                  name: '李女士',
                  role: '电视剧爱好者',
                  content:
                    '追剧必备！分类清晰，找剧方便。最让我惊喜的是动漫资源也很全，孩子们特别喜欢在这里看动画片。全家人都在用蘑菇影视。',
                  rating: 5,
                },
                {
                  name: '王先生',
                  role: '综艺节目粉丝',
                  content:
                    '综艺更新速度很快，画质清晰不卡顿。界面设计简洁明了，操作也很方便。用了很多视频网站，最后选择了蘑菇影视，真的很不错。',
                  rating: 4,
                },
              ].map((review, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-lg p-5 md:p-6"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i
                          className={`text-sm ${
                            i < review.rating
                              ? 'ri-star-fill text-primary-400'
                              : 'ri-star-line text-foreground-300'
                          }`}
                        ></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{review.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center">
                      <span className="text-xs font-bold text-secondary-600">
                        {review.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">
                        {review.name}
                      </p>
                      <p className="text-xs text-foreground-500">{review.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-secondary-500 rounded-full"></div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                {t('about.contact')}
              </h2>
            </div>

            <div className="bg-background-100 rounded-lg p-6 md:p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-mail-line text-lg text-primary-500"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground-900 mb-1">
                      电子邮箱
                    </h4>
                    <a
                      href={`mailto:${t('about.contactEmail')}`}
                      className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200"
                    >
                      {t('about.contactEmail')}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-phone-line text-lg text-accent-500"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground-900 mb-1">
                      客服电话
                    </h4>
                    <p className="text-sm text-foreground-600">
                      {t('about.contactPhone')}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center shrink-0">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-map-pin-line text-lg text-secondary-500"></i>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-foreground-900 mb-1">
                      公司地址
                    </h4>
                    <p className="text-sm text-foreground-600">
                      {t('about.contactAddress')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}